import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AuthDialog } from './components/AuthDialog';
import { AuthDialogContext, type AuthDialogOptions, type AuthMode } from './auth-dialog-context';

/**
 * Owns the sign-in dialog and the one piece of state it needs: where to send the
 * user afterwards.
 *
 * Lives INSIDE the router rather than beside the QueryClient, because closing
 * and succeeding both navigate. Everything under RootLayout can therefore call
 * `useAuthDialog()`, which is every page.
 */
export function AuthDialogProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [mode, setModeState] = useState<AuthMode | null>(null);

  // Held in a ref, not state: changing where to redirect must not re-render the
  // form the user is halfway through typing into.
  const optionsRef = useRef<AuthDialogOptions>({});

  const open = useCallback((next: AuthMode, options: AuthDialogOptions = {}) => {
    optionsRef.current = options;
    setModeState(next);
  }, []);

  const setMode = useCallback((next: AuthMode) => setModeState(next), []);

  const close = useCallback(() => {
    const { closeTo } = optionsRef.current;

    setModeState(null);
    optionsRef.current = {};

    if (closeTo) {
      void navigate(closeTo, { replace: true });
    }
  }, [navigate]);

  const onAuthenticated = useCallback(() => {
    const { next, closeTo } = optionsRef.current;

    setModeState(null);
    optionsRef.current = {};

    // `next` wins over `closeTo`: someone who was sent here by a guard should
    // land on the page they were trying to reach, not back at the start.
    const destination = next ?? closeTo;

    if (destination) {
      void navigate(destination, { replace: true });
    }
  }, [navigate]);

  const value = useMemo(() => ({ mode, open, setMode, close }), [mode, open, setMode, close]);

  return (
    <AuthDialogContext.Provider value={value}>
      {children}
      {mode ? (
        <AuthDialog
          mode={mode}
          onModeChange={setMode}
          onClose={close}
          onAuthenticated={onAuthenticated}
        />
      ) : null}
    </AuthDialogContext.Provider>
  );
}
